// A21NISTBuildingYourTeam.jsx
import PropTypes from "prop-types";
import ArticleLayout from "../../layout/ArticleLayout";
import { Paragraph, H2, H3 } from "../../styles/AppStyles";
import { LINKS, href } from "../../config/links";

const A21NISTBuildingYourTeam = ({ istouch, isdarkmode }) => {
  const title =
    "NIST Webinar Recap: Building Your Small Business Cybersecurity Team — Free Resources That Actually Help";
  const heroImageSrc = "/images/articles/nist-smb-team.png";

  return (
    <ArticleLayout
      istouch={istouch}
      isdarkmode={isdarkmode}
      title={title}
      canonicalUrl={href(LINKS.ARTICLE.NIST_BUILDING_SMB_TEAM)}
      description="On May 5, 2026, NIST hosted a webinar on building small business cybersecurity teams — from in-house staff to outsourcing. This recap covers the recommended free resources for SMBs, including Cybersecurity Clinics, the Cyber Readiness Institute, the FTC, and more."
      ogTitle={title}
      ogDescription="NIST's May 2026 webinar on building small business cybersecurity teams highlighted free resources every SMB should know about — pro bono assessments, self-paced training programs, policy templates, and government guidance."
      ogImage={heroImageSrc}
      heroImageSrc={heroImageSrc}
      heroImageAlt="Illustration representing a small business cybersecurity team"
      publishedLabel="Published: May 5, 2026"
      lastUpdatedLabel="Last updated: May 5, 2026"
    >
      <Paragraph>
        On May 5, 2026, the National Institute of Standards and Technology (NIST) hosted a
        webinar titled <em>Building Your Small Business Cybersecurity Team: From In-House to
        Outsourcing</em>. The session addressed one of the most practical challenges small
        organizations face: how do you build cybersecurity capability when you don't have a
        dedicated security team, a large IT budget, or in-house expertise?
      </Paragraph>

      <Paragraph>
        The webinar covered staffing models, the tradeoffs between in-house and outsourced
        security functions, and — most usefully for many attendees — a curated list of free
        resources designed specifically for small and medium-sized businesses. This article
        summarizes those resources and explains what each one offers.
      </Paragraph>

      <Paragraph>
        The full slide deck is available directly from NIST:{" "}
        <a
          href="https://www.nist.gov/system/files/documents/2026/05/05/Building_SMB_Cyber_Team_May-5-2026.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Building Your Small Business Cybersecurity Team (PDF)
        </a>
        .
      </Paragraph>

      <H2>The Core Challenge: You Can't Hire Your Way Out of This</H2>

      <Paragraph>
        Most small businesses cannot afford a full-time
        security team. A single experienced cybersecurity professional costs $90,000–$130,000
        per year — and one person cannot cover the full scope of a mature security program.
        The assumption that cybersecurity requires dedicated, specialized headcount leaves many
        small businesses paralyzed: they know they need to act, but they don't know how to
        act within real-world constraints.
      </Paragraph>

      <Paragraph>
        NIST's framing is more practical than that. Cybersecurity is not purely a staffing
        problem — it is a <em>program</em> problem. What most small businesses are missing
        is not bodies, but structure: a documented plan, assigned responsibilities, and a set
        of controls that match the organization's actual risk profile. The resources below are
        specifically designed to help you build that structure without requiring dedicated
        security staff to do it.
      </Paragraph>

      <H2>In-House vs. Outsourcing: The Basic Tradeoff</H2>

      <Paragraph>
        Small businesses typically end up with some combination of three approaches:
      </Paragraph>

      <ul>
        <li>
          <strong>In-house ownership with external tools.</strong> A staff member — often the
          IT generalist, operations manager, or even the owner — takes responsibility for
          cybersecurity planning using frameworks, templates, and training resources. This is
          the lowest cost option and works well when the person has time, organizational
          authority, and access to the right tools.
        </li>
        <li>
          <strong>Managed Security Service Providers (MSSPs) or MSPs with security
          capabilities.</strong> A third-party provider handles monitoring, patching, endpoint
          protection, and sometimes policy management. Cost varies widely — from $500 to
          several thousand dollars per month for a small organization — and quality varies even
          more. See{" "}
          <a href={LINKS.CONSULTANTS.MANAGED_SERVICE_PROVIDERS}>
            Working with Managed Service Providers
          </a>{" "}
          for guidance on evaluating MSP relationships.
        </li>
        <li>
          <strong>Fractional or virtual CISOs (vCISOs).</strong> An experienced security
          professional is engaged part-time to oversee the program, set policy, manage
          vendors, and handle incident response planning. This is increasingly common among
          organizations that need strategic direction but can't justify a full-time hire.
        </li>
      </ul>

      <Paragraph>
        For most small businesses, the right answer is in-house ownership of the program
        paired with selective outsourcing of technical functions — monitoring, patching,
        backups — where specialized skills and tooling matter most. The free resources below
        are designed to support exactly that model.
      </Paragraph>

      <H2>Free Resources Recommended by NIST</H2>

      <H3>Cybersecurity Clinics</H3>

      <Paragraph>
        <a href="https://cybersecurityclinics.org/" target="_blank" rel="noreferrer">
          cybersecurityclinics.org
        </a>
      </Paragraph>

      <Paragraph>
        Cybersecurity Clinics are university-based programs that provide pro bono cybersecurity
        services to small businesses. There are currently 60
        clinics operating across 8 countries, with 44 locations in the United States. Students
        supervised by faculty and practitioners do the work — at no cost to the organization
        receiving help.
      </Paragraph>

      <Paragraph>
        Services vary by clinic but commonly include:
      </Paragraph>

      <ul>
        <li>Vulnerability and risk assessments</li>
        <li>Cybersecurity policy development and templates</li>
        <li>Incident response plan creation</li>
        <li>Ransomware preparedness training</li>
        <li>NIST CSF and CMMC alignment support</li>
      </ul>

      <Paragraph>
        If you are a small organization that has been putting off a risk assessment because of
        cost, this is the most direct path to getting professional-grade help without paying
        for it. Find your nearest clinic through the directory at{" "}
        <a href="https://cybersecurityclinics.org/" target="_blank" rel="noreferrer">
          cybersecurityclinics.org
        </a>
        .
      </Paragraph>

      <H3>Cyber Readiness Institute</H3>

      <Paragraph>
        <a href="https://cyberreadinessinstitute.org/" target="_blank" rel="noreferrer">
          cyberreadinessinstitute.org
        </a>
      </Paragraph>

      <Paragraph>
        The Cyber Readiness Institute (CRI) offers a free, self-paced online program built
        specifically for organizations without dedicated IT or security staff. The program is
        designed around behavioral change — it focuses on practical implementation rather than
        theory.
      </Paragraph>

      <Paragraph>
        What CRI provides at no cost:
      </Paragraph>

      <ul>
        <li>
          <strong>Cyber Readiness Program.</strong> A structured, self-paced online program
          covering the controls that matter most for small businesses, with editable policy
          templates and communication kits included.
        </li>
        <li>
          <strong>Cyber Leader Certification.</strong> A training and certification program for
          the person designated as responsible for cybersecurity within the organization — no
          prior technical background required.
        </li>
        <li>
          <strong>Cyber Readiness Playbook.</strong> Available in multiple languages, covering
          the essentials of building a cybersecurity program.
        </li>
        <li>
          <strong>Specialized programs.</strong> Sector-specific programs for water utilities
          (critical infrastructure) and manufacturers (CYMANII pilot), with additional
          resources on AI risk, remote work, incident response, and cyber insurance.
        </li>
      </ul>

      <Paragraph>
        CRI's content is notably jargon-free and practical. If you need a starting point for
        someone who has been handed cybersecurity responsibility without a security background,
        the Cyber Leader Certification is one of the best free options available.
      </Paragraph>

      <H3>National Cybersecurity Alliance — Stay Safe Online</H3>

      <Paragraph>
        <a href="https://www.staysafeonline.org/" target="_blank" rel="noreferrer">
          staysafeonline.org
        </a>
      </Paragraph>

      <Paragraph>
        The National Cybersecurity Alliance (NCA) runs Stay Safe Online, which maintains a
        library of hundreds of free resources aimed at individuals and small businesses.
        Their <strong>CyberSecure My Business</strong> program is designed specifically for
        small enterprises managing cybersecurity without dedicated staff.
      </Paragraph>

      <Paragraph>
        Free resources include:
      </Paragraph>

      <ul>
        <li>Toolkits on cybercrime, data privacy, and small business security</li>
        <li>Videos, infographics, and practical written guides</li>
        <li>Seasonal awareness content (e.g., cybersecurity tips during tax season)</li>
        <li>Webinars covering cyber insurance, MSP selection, and security awareness</li>
      </ul>

      <Paragraph>
        NCA also runs <strong>Cybersecurity Awareness Month</strong> each October, which
        produces a significant volume of free training and awareness materials that
        organizations can use directly with their staff.
      </Paragraph>

      <H3>America's Small Business Development Centers (SBDC)</H3>

      <Paragraph>
        <a href="https://americassbdc.org/" target="_blank" rel="noreferrer">
          americassbdc.org
        </a>
      </Paragraph>

      <Paragraph>
        America's SBDC is a nationwide network of small business advisors funded in part by
        the Small Business Administration (SBA). Most people know SBDCs for business planning
        and financing help — but they also maintain a cybersecurity resource hub, and many
        local SBDC advisors can connect you to cybersecurity-specific guidance and local
        resources.
      </Paragraph>

      <Paragraph>
        SBDCs are free to use. If you are a small business that already works with an SBDC
        advisor, asking them about cybersecurity resources in your region is a low-friction
        starting point.
      </Paragraph>

      <H3>FTC Small Business Cybersecurity Resources</H3>

      <Paragraph>
        <a
          href="https://www.ftc.gov/business-guidance/small-businesses/cybersecurity"
          target="_blank"
          rel="noreferrer"
        >
          ftc.gov/business-guidance/small-businesses/cybersecurity
        </a>
      </Paragraph>

      <Paragraph>
        The Federal Trade Commission maintains a practical cybersecurity guidance hub for
        small businesses covering data security, breach notification requirements, scam
        prevention, and the basics of protecting customer data. The content is written for
        business owners rather than technical staff.
      </Paragraph>

      <Paragraph>
        Particularly useful sections include guidance on what to do after a data breach, how
        to evaluate and secure third-party vendor access, and how to train staff on
        recognizing phishing and social engineering. The broader small business guidance page
        at{" "}
        <a
          href="https://www.ftc.gov/business-guidance/small-businesses"
          target="_blank"
          rel="noreferrer"
        >
          ftc.gov/business-guidance/small-businesses
        </a>{" "}
        also covers privacy, advertising, and credit practices.
      </Paragraph>

      <H3>FTC Bulk Order — Free Printed Materials</H3>

      <Paragraph>
        <a href="https://www.bulkorder.ftc.gov/" target="_blank" rel="noreferrer">
          bulkorder.ftc.gov
        </a>
      </Paragraph>

      <Paragraph>
        The FTC offers free printed materials that can be ordered in bulk through their bulk
        order site. For organizations that serve communities or run in-person training — local
        governments, nonprofits, schools — physical handouts on cybersecurity basics,
        phishing awareness, and privacy rights can be ordered at no cost and no minimum.
        These are particularly useful for staff training sessions where not everyone is
        comfortable with digital-only content.
      </Paragraph>

      <H2>What These Resources Cover — and What They Don't</H2>

      <Paragraph>
        These free resources are excellent for:
      </Paragraph>

      <ul>
        <li>Getting a risk assessment when you can't afford to pay for one</li>
        <li>Training a non-technical staff member to own the security program</li>
        <li>Finding policy templates and awareness materials</li>
        <li>Understanding compliance and breach notification requirements</li>
        <li>Building foundational security awareness across your team</li>
      </ul>

      <Paragraph>
        What they generally don't replace:
      </Paragraph>

      <ul>
        <li>
          A structured, complete cybersecurity plan that covers your specific organization —
          your assets, your risks, your policies, your business continuity and incident
          response procedures
        </li>
        <li>
          Ongoing tracking of controls, risks, and annual reviews
        </li>
        <li>
          Document generation: your policies, disaster recovery plan, incident response plan,
          and vendor agreements need to be customized to your organization, not generic templates
        </li>
      </ul>

      <Paragraph>
        Free resources give you knowledge and starting points. Converting that into a working,
        documented, maintained cybersecurity program is the harder part — and it's where most
        small businesses stall.
      </Paragraph>

      <H2>Where CSFSafe Fits In</H2>

      <Paragraph>
        The{" "}
        <a href={LINKS.MARKETING.PLAYBOOK}>CSFSafe Cybersecurity Playbook</a> is designed for
        exactly the organizations these NIST resources serve: small businesses, nonprofits, and
        local governments that need to build a complete, documented cybersecurity program
        without a full-time security team.
      </Paragraph>

      <Paragraph>
        Where the free resources listed above provide knowledge, awareness, and starting-point
        templates, CSFSafe provides the structure to go from those starting points to a
        finished, working program:
      </Paragraph>

      <ul>
        <li>
          Guided step-by-step process through all ten components of a cybersecurity program —
          team identification, asset inventory, risk analysis, policies, BC/DR, incident
          response, training, audits, and annual reviews
        </li>
        <li>
          Document generation: cybersecurity policy, incident response plan, disaster recovery
          plan, and business continuity plan — customized to your organization
        </li>
        <li>
          Risk register and asset inventory built in, not bolt-on
        </li>
        <li>
          Designed to run on your device — your data stays local
        </li>
      </ul>

      <Paragraph>
        The free resources from NIST, the Cyber Readiness Institute, and the FTC are worth
        using. CSFSafe is where you take what you've learned and turn it into an actual plan.
        Learn more at the{" "}
        <a href={LINKS.MARKETING.PLAYBOOK}>CSFSafe Cybersecurity Playbook</a> page, or start
        with the free{" "}
        <a href={LINKS.CHECKUP}>Cybersecurity Checkup</a> to see where your organization
        stands today.
      </Paragraph>

      <H2>Further Reading</H2>

      <ul>
        <li>
          <a
            href="https://www.nist.gov/system/files/documents/2026/05/05/Building_SMB_Cyber_Team_May-5-2026.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Building Your Small Business Cybersecurity Team: From In-House to Outsourcing (NIST, May 2026, PDF)
          </a>
        </li>
        <li>
          <a href={LINKS.ARTICLE.NISTCSFSMALLBUSINESS}>
            The NIST Cybersecurity Framework for Small Businesses
          </a>{" "}
          — CSFSafe Learning Center
        </li>
        <li>
          <a href={LINKS.CONSULTANTS.MANAGED_SERVICE_PROVIDERS}>
            Working with Managed Service Providers
          </a>{" "}
          — CSFSafe Learning Center
        </li>
        <li>
          <a href={LINKS.CONSULTANTS.WORKING_WITH_CONSULTANTS}>
            Working with Cybersecurity Consultants
          </a>{" "}
          — CSFSafe Learning Center
        </li>
        <li>
          <a
            href="https://www.nist.gov/cyberframework"
            target="_blank"
            rel="noreferrer"
          >
            NIST Cybersecurity Framework (CSF 2.0)
          </a>
        </li>
        <li>
          <a
            href="https://www.cisa.gov/resources-tools/resources/free-cybersecurity-services-and-tools"
            target="_blank"
            rel="noreferrer"
          >
            Free Cybersecurity Services and Tools (CISA)
          </a>
        </li>
      </ul>
    </ArticleLayout>
  );
};

A21NISTBuildingYourTeam.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default A21NISTBuildingYourTeam;
